import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import type { League, PlayerPool, TennisMatchPerformance } from '../../types/database';
import { calculateTennisMatchPoints, DEFAULT_TENNIS_SCORING_CONFIG } from '../../utils/pointsCalculation';
import { deriveSetFlags } from '../../utils/tennisMatchPerformance';
import { flagEmoji } from '../../utils/countryFlags';
import { tennisPlayerPoolService } from '../../services/firestore';

type TennisRound = 'R128' | 'R64' | 'R32' | 'R16' | 'QF' | 'SF' | 'Final';

interface SetScore {
  gamesWon: number;
  gamesLost: number;
}

interface TennisMatchResultDialogProps {
  open: boolean;
  onClose: () => void;
  leagues: League[];
  playerPools: PlayerPool[];
  onSuccess: (message: string) => void;
}

/**
 * Admin dialog for entering tennis match results and computing points.
 * Mirrors the ScorecardParserDialog pattern for cricket.
 */
const TennisMatchResultDialog: React.FC<TennisMatchResultDialogProps> = ({
  open,
  onClose,
  leagues,
  playerPools,
  onSuccess,
}) => {
  const [selectedLeagueId, setSelectedLeagueId] = useState('');
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [opponentName, setOpponentName] = useState('');
  const [opponentSeed, setOpponentSeed] = useState<number | ''>('');
  const [round, setRound] = useState<TennisRound>('R64');
  const [won, setWon] = useState(true);
  const [opponentWithdrew, setOpponentWithdrew] = useState(false);
  const [playerRetired, setPlayerRetired] = useState(false);
  const [setScores, setSetScores] = useState<SetScore[]>([{ gamesWon: 6, gamesLost: 4 }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<number | null>(null);

  const selectedLeague = leagues.find(l => l.id === selectedLeagueId && l.sport === 'tennis');
  const selectedPool = selectedLeague?.playerPoolId
    ? playerPools.find(p => p.id === selectedLeague.playerPoolId)
    : null;
  const poolPlayers = selectedPool?.players ?? [];

  const selectedPlayer = poolPlayers.find(p => p.playerId === selectedPlayerId);

  // Gender-aware set limits: women best-of-3, men best-of-5
  const maxSets = selectedPlayer?.gender === 'women' ? 3 : 5;
  const setsToWin = Math.ceil(maxSets / 2);

  // Withdrawal/retirement overrides the won flag for point calculation
  const effectiveWon = won || opponentWithdrew || playerRetired;

  // Auto-derive from entered scores — eliminates manual checkbox errors.
  // Shared with the pool page + API import via deriveSetFlags. Gender is
  // passed so the final-set tie-break bonus is only awarded when the
  // tie-break was in the actual DECIDING set (set 5 men / set 3 women).
  const { straightSets: autoStraightSets, finalSetTieBreak: autoFinalSetTieBreak } =
    deriveSetFlags(setScores, effectiveWon, opponentWithdrew, selectedPlayer?.gender);

  const computePreview = () => {
    if (!selectedPlayer) return;
    const config = selectedPool?.tennisConfig ?? selectedLeague?.tennisConfig ?? DEFAULT_TENNIS_SCORING_CONFIG;
    const setsWonGames = setScores.map(s => s.gamesWon);
    const setsLostGames = setScores.map(s => s.gamesLost);
    const pts = calculateTennisMatchPoints(
      effectiveWon,
      autoStraightSets,
      autoFinalSetTieBreak,
      setsWonGames,
      setsLostGames,
      selectedPlayer.seed,
      opponentSeed !== '' ? opponentSeed : undefined,
      config
    );
    setPreview(pts);
  };

  const handleApply = async () => {
    if (!selectedPool || !selectedPlayer) return;
    setLoading(true);
    setError('');
    try {
      const config = selectedPool?.tennisConfig ?? selectedLeague?.tennisConfig ?? DEFAULT_TENNIS_SCORING_CONFIG;
      const setsWonGames = setScores.map(s => s.gamesWon);
      const setsLostGames = setScores.map(s => s.gamesLost);
      const pts = calculateTennisMatchPoints(
        effectiveWon,
        autoStraightSets,
        autoFinalSetTieBreak,
        setsWonGames,
        setsLostGames,
        selectedPlayer.seed,
        opponentSeed !== '' ? opponentSeed : undefined,
        config
      );

      const matchLabel = `${round} vs ${opponentName || 'Opponent'}`;

      const perf: TennisMatchPerformance = {
        id: `${Date.now()}-${selectedPlayerId}`,
        round,
        opponentName: opponentName.trim() || undefined,
        opponentSeed: opponentSeed !== '' ? opponentSeed : undefined,
        won: effectiveWon,
        setScores: opponentWithdrew ? [] : setScores.map(s => ({ gamesWon: s.gamesWon, gamesLost: s.gamesLost })),
        straightSets: autoStraightSets,
        finalSetTieBreak: autoFinalSetTieBreak,
        pointsEarned: pts,
        source: 'manual',
        date: new Date(),
      };

      await tennisPlayerPoolService.addMatchPerformance(
        selectedPool.id,
        selectedPlayerId,
        perf,
        `Tennis: ${selectedPlayer.name} — ${matchLabel}`
      );

      onSuccess(`+${pts} pts applied to ${selectedPlayer.name} for ${matchLabel}`);
      handleReset();
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to apply match result');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedLeagueId('');
    setSelectedPlayerId('');
    setOpponentName('');
    setOpponentSeed('');
    setRound('R64');
    setWon(true);
    setOpponentWithdrew(false);
    setPlayerRetired(false);
    setSetScores([{ gamesWon: 6, gamesLost: 4 }]);
    setPreview(null);
    setError('');
  };

  const addSet = () => setSetScores(prev => [...prev, { gamesWon: 6, gamesLost: 4 }]);
  const removeSet = (i: number) => setSetScores(prev => prev.filter((_, idx) => idx !== i));
  const updateSet = (i: number, field: keyof SetScore, value: number) => {
    setSetScores(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s));
  };

  const tennisLeagues = leagues.filter(l => l.sport === 'tennis');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Enter Tennis Match Result</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>

          {/* League & Player Selection */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required size="small">
                <InputLabel>Tennis League</InputLabel>
                <Select
                  value={selectedLeagueId}
                  label="Tennis League"
                  onChange={e => { setSelectedLeagueId(e.target.value); setSelectedPlayerId(''); setPreview(null); }}
                >
                  {tennisLeagues.length === 0 && <MenuItem value="" disabled>No tennis leagues found</MenuItem>}
                  {tennisLeagues.map(l => (
                    <MenuItem key={l.id} value={l.id}>{l.name} — {l.tournamentName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required size="small" disabled={!selectedPool}>
                <InputLabel>Player</InputLabel>
                <Select
                  value={selectedPlayerId}
                  label="Player"
                  onChange={e => { setSelectedPlayerId(e.target.value); setPreview(null); }}
                >
                  {poolPlayers.map(p => (
                    <MenuItem key={p.playerId} value={p.playerId}>
                      {flagEmoji(p.team)} {p.name} {p.seed !== undefined ? `[${p.seed}]` : ''} · {p.gender ?? p.role} · {p.points} pts
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Match Details */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Round</InputLabel>
                <Select value={round} label="Round" onChange={e => setRound(e.target.value as TennisRound)}>
                  {(['R128', 'R64', 'R32', 'R16', 'QF', 'SF', 'Final'] as TennisRound[]).map(r => (
                    <MenuItem key={r} value={r}>{r}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth size="small"
                label="Opponent Name"
                value={opponentName}
                onChange={e => setOpponentName(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth size="small"
                label="Opponent Seed (optional)"
                type="number"
                value={opponentSeed}
                onChange={e => setOpponentSeed(e.target.value === '' ? '' : parseInt(e.target.value))}
                inputProps={{ min: 1 }}
              />
            </Grid>
          </Grid>

          {/* Match Outcome */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>Match Outcome</Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <FormControlLabel
                control={<Checkbox checked={won} onChange={e => { setWon(e.target.checked); if (e.target.checked) { setOpponentWithdrew(false); setPlayerRetired(false); } }} />}
                label="Won"
                disabled={opponentWithdrew || playerRetired}
              />
              {!opponentWithdrew && (
                <FormControlLabel
                  control={<Checkbox checked={playerRetired} onChange={e => { setPlayerRetired(e.target.checked); if (e.target.checked) setOpponentWithdrew(false); }} />}
                  label="Player retired mid-match"
                />
              )}
              {!playerRetired && (
                <FormControlLabel
                  control={<Checkbox checked={opponentWithdrew} onChange={e => { setOpponentWithdrew(e.target.checked); if (e.target.checked) setPlayerRetired(false); }} />}
                  label="Opponent withdrew / walkover"
                />
              )}
              {selectedPlayer && (
                <Chip
                  size="small"
                  label={selectedPlayer.gender === 'women' ? '♀ Women — Best of 3' : '♂ Men — Best of 5'}
                  variant="outlined"
                  sx={{ fontSize: '0.72rem' }}
                />
              )}
            </Box>
            {opponentWithdrew && (
              <Alert severity="info" sx={{ mt: 1, py: 0.5, fontSize: '0.8rem' }}>
                Opponent withdrew — counted as a straight set win.
              </Alert>
            )}
            {playerRetired && (
              <Alert severity="info" sx={{ mt: 1, py: 0.5, fontSize: '0.8rem' }}>
                Player retired — win awarded, points calculated from sets played.
              </Alert>
            )}
            {effectiveWon && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                <Chip
                  size="small"
                  label={`Straight Sets: ${autoStraightSets ? 'Yes ✓' : 'No'}`}
                  color={autoStraightSets ? 'success' : 'default'}
                  variant={autoStraightSets ? 'filled' : 'outlined'}
                  sx={{ fontSize: '0.72rem' }}
                />
                <Chip
                  size="small"
                  label={`Tie-Break: ${autoFinalSetTieBreak ? 'Yes ✓' : 'No'}`}
                  color={autoFinalSetTieBreak ? 'info' : 'default'}
                  variant={autoFinalSetTieBreak ? 'filled' : 'outlined'}
                  sx={{ fontSize: '0.72rem' }}
                />
              </Box>
            )}
          </Box>

          {/* Set Scores — hidden for opponent withdrawal (no sets played) */}
          <Box sx={{ display: opponentWithdrew ? 'none' : undefined }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box>
                <Typography variant="subtitle2" component="span">Set Scores</Typography>
                {selectedPlayer && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    Best of {maxSets} — first to {setsToWin} sets
                  </Typography>
                )}
              </Box>
              <Button size="small" onClick={addSet} disabled={setScores.length >= maxSets}>
                + Add Set
              </Button>
            </Box>
            {setScores.map((s, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography variant="body2" sx={{ minWidth: 48 }}>Set {i + 1}</Typography>
                <TextField
                  size="small" type="number" label="Won" value={s.gamesWon}
                  onChange={e => updateSet(i, 'gamesWon', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0, max: 7 }} sx={{ width: 80 }}
                />
                <Typography>–</Typography>
                <TextField
                  size="small" type="number" label="Lost" value={s.gamesLost}
                  onChange={e => updateSet(i, 'gamesLost', parseInt(e.target.value) || 0)}
                  inputProps={{ min: 0, max: 7 }} sx={{ width: 80 }}
                />
                {setScores.length > 1 && (
                  <Button size="small" color="error" onClick={() => removeSet(i)}>Remove</Button>
                )}
              </Box>
            ))}
          </Box>

          {/* Points Preview */}
          {preview !== null && (
            <Alert severity="info">
              Computed points: <strong>+{preview} pts</strong> for {selectedPlayer?.name}
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { handleReset(); onClose(); }}>Cancel</Button>
        <Button onClick={computePreview} disabled={!selectedPlayer}>
          Preview Points
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={loading || !selectedPlayer || !selectedPool}
          startIcon={loading ? <CircularProgress size={18} /> : null}
        >
          Apply Result
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TennisMatchResultDialog;
